import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PageTitle, PageContent, Button } from 'nallian-shared-ui/lib/components';
import { GetRights, GetUserId } from 'nallian-shared-ui/lib/selectors';
import { hasRight, hasSomeRights } from 'nallian-shared-ui/lib/lib/routesHelper';
import { Table, Params, Actions, Menu, Merge } from 'nallian-table-builder-ui';
import { INFINITE_SCROLL, PRINCIPALS_RESOURCE } from '../constants';
import { PRINCIPAL_STATES } from '../../organisations/constants';
import PrincipalState from '../../organisations/components/principals/table/PrincipalState';
import PrincipalStateFilter from '../../organisations/components/principals/table/PrincipalStateFilter';
import ConditionalMenu from '../../organisations/components/principals/table/ConditionalMenu';
import apiRoutes from '../../../app/api/routes';

import ImpersonationRequestModal from './impersonation/ImpersonationRequestModal';
import ImpersonationRequestsModal from '../../impersonationRequests/components/ImpersonationRequestsModal';

export const PRINCIPAL_RIGHTS = [
  'PRINCIPALS.MANAGE.ALL',
  'PRINCIPALS.MANAGE.ALL.EXCEPTSYSTEMORGS',
  'PRINCIPALS.MANAGE.ORGANISATION',
];

const Principals = ({ openImpersonationRequests }) => {
  const { t } = useTranslation();
  const rights = useSelector(GetRights);
  const identityId = useSelector(GetUserId);
  const navigate = useNavigate();

  const canCreateImpersonation = hasRight(rights, 'impersonation-requests/create');
  const canReadImpersonationRequests = hasSomeRights(rights, [
    'impersonation-requests/read/own',
    'impersonation-requests/read/all',
  ]);

  const [impersonationTarget, setImpersonationTarget] = useState(null);

  const openRequests = useCallback(() => navigate('/principals/impersonation-requests'), [
    navigate,
  ]);
  const closeRequests = useCallback(() => navigate('/principals'), [navigate]);

  const handleRequestImpersonation = useCallback((_, item) => setImpersonationTarget(item), []);
  const handleCloseImpersonationModal = useCallback(() => setImpersonationTarget(null), []);

  return (
    <>
      <PageTitle>{t('allUsers')}</PageTitle>
      <PageContent>
        {canReadImpersonationRequests && (
          <Button btnprimary content={t('impersonationRequests')} onClick={openRequests} />
        )}
        <Table
          infiniteScroll={INFINITE_SCROLL}
          resource={PRINCIPALS_RESOURCE}
          url={apiRoutes.principals()}
          noResultsMessage={t('noUsersFound')}
          pageSize={50}
          filtered
          sorted
          noUserSettings
          resizable
        >
          <Actions.Link width="2.5rem" align="center" icon="eye" url="/principals/:id" />
          <Params.String name="fullName" width="16rem" minWidth="16rem" grow />
          <Params.String
            name="primaryEmailAddress"
            label="email"
            width="16rem"
            minWidth="16rem"
            grow
          />
          <Params.String
            name="organisation.name"
            label="organisation"
            filterName="organisationName"
            link="/organisations/:organisation.id"
            width="16rem"
            minWidth="16rem"
            noSort
            grow
          />
          <Params.Custom
            filter={PrincipalStateFilter}
            content={PrincipalState}
            label="state"
            name="activeState"
            width="16rem"
            grow
            noSort
          />
          {canCreateImpersonation && (
            <Merge width="3.5rem" align="center" as={ConditionalMenu}>
              <Menu width="3.5rem" align="center">
                <Actions.Button
                  label={t('requestImpersonation')}
                  onClick={handleRequestImpersonation}
                  state={PRINCIPAL_STATES.ACTIVE}
                />
              </Menu>
            </Merge>
          )}
        </Table>
      </PageContent>
      <ImpersonationRequestModal
        open={!!impersonationTarget}
        targetPrincipal={impersonationTarget}
        onClose={handleCloseImpersonationModal}
      />
      <ImpersonationRequestsModal
        open={!!openImpersonationRequests}
        onClose={closeRequests}
        identityId={identityId}
      />
    </>
  );
};

Principals.propTypes = {
  openImpersonationRequests: PropTypes.bool,
};

export default Principals;
